import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Loader from '../../components/Loader';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import { Settings } from '../../client/services';

export const REWARD_POINTS_SETTING_NAME = 'reward_points_config';

export type RewardPointsFormValues = {
  earnSpendAmount: number;
  earnPoints: number;
  /** Admin UI: how many candles equal 1 QAR discount (saved as redeemPointValue = 1/candlesPerQar). */
  candlesPerQar: number;
  maxRedeemPointsPerOrder: number;
  birthdayRewardPoints: number;
  birthdayRewardDaysBefore: number;
  referralReferrerPoints: number;
  referralRefereePoints: number;
  pointsExpirationDays: number;
  referralSignupExpiryDays: number;
  activeState: boolean;
};

type NumberFieldKey = Exclude<keyof RewardPointsFormValues, 'activeState'>;

export const DEFAULT_REWARD_POINTS_FORM: RewardPointsFormValues = {
  earnSpendAmount: 10,
  earnPoints: 1,
  candlesPerQar: 40,
  maxRedeemPointsPerOrder: 0,
  birthdayRewardPoints: 0,
  birthdayRewardDaysBefore: 6,
  referralReferrerPoints: 0,
  referralRefereePoints: 0,
  pointsExpirationDays: 0,
  referralSignupExpiryDays: 60,
  activeState: true,
};

const NEGATIVE_ERROR = 'Negative values are not allowed';

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** Convert stored QAR-per-candle into candles-per-1-QAR for the admin form. */
export const redeemValueToCandlesPerQar = (redeemPointValue: number): number => {
  if (!redeemPointValue || redeemPointValue <= 0) {
    return DEFAULT_REWARD_POINTS_FORM.candlesPerQar;
  }
  const raw = 1 / redeemPointValue;
  if (Math.abs(raw - Math.round(raw)) < 0.0001) {
    return Math.round(raw);
  }
  return Math.round(raw * 10000) / 10000;
};

/** Convert admin candles-per-1-QAR into backend redeemPointValue (QAR per candle). */
export const candlesPerQarToRedeemValue = (candlesPerQar: number): number => {
  if (!candlesPerQar || candlesPerQar <= 0) return 0;
  return parseFloat((1 / candlesPerQar).toFixed(6));
};

export const parseRewardPointsValue = (
  raw?: string | null
): RewardPointsFormValues => {
  if (!raw) return { ...DEFAULT_REWARD_POINTS_FORM };
  try {
    const parsed = JSON.parse(raw);
    const referral = parsed?.referralRewardPoints || {};
    const normalize = (value: unknown, fallback: number) => {
      const num = toNumber(value, fallback);
      return num < 0 ? 0 : num;
    };

    const redeemPointValue = normalize(
      parsed?.redeemPointValue,
      candlesPerQarToRedeemValue(DEFAULT_REWARD_POINTS_FORM.candlesPerQar)
    );

    return {
      earnSpendAmount:
        normalize(
          parsed?.earnSpendAmount,
          DEFAULT_REWARD_POINTS_FORM.earnSpendAmount
        ) || DEFAULT_REWARD_POINTS_FORM.earnSpendAmount,
      earnPoints: normalize(
        parsed?.earnPoints,
        DEFAULT_REWARD_POINTS_FORM.earnPoints
      ),
      candlesPerQar: redeemValueToCandlesPerQar(redeemPointValue),
      maxRedeemPointsPerOrder: normalize(
        parsed?.maxRedeemPointsPerOrder,
        DEFAULT_REWARD_POINTS_FORM.maxRedeemPointsPerOrder
      ),
      birthdayRewardPoints: normalize(
        parsed?.birthdayRewardPoints,
        DEFAULT_REWARD_POINTS_FORM.birthdayRewardPoints
      ),
      birthdayRewardDaysBefore: normalize(
        parsed?.birthdayRewardDaysBefore,
        DEFAULT_REWARD_POINTS_FORM.birthdayRewardDaysBefore
      ),
      referralReferrerPoints: normalize(
        parsed?.referralReferrerPoints ??
          referral?.referrer ??
          referral?.referringCustomer,
        DEFAULT_REWARD_POINTS_FORM.referralReferrerPoints
      ),
      referralRefereePoints: normalize(
        parsed?.referralRefereePoints ??
          referral?.referee ??
          referral?.referredCustomer,
        DEFAULT_REWARD_POINTS_FORM.referralRefereePoints
      ),
      pointsExpirationDays: normalize(
        parsed?.pointsExpirationDays,
        DEFAULT_REWARD_POINTS_FORM.pointsExpirationDays
      ),
      referralSignupExpiryDays: normalize(
        parsed?.referralSignupExpiryDays,
        DEFAULT_REWARD_POINTS_FORM.referralSignupExpiryDays
      ),
      activeState: parsed?.activeState !== false,
    };
  } catch {
    return { ...DEFAULT_REWARD_POINTS_FORM };
  }
};

const RewardPointsSettingsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settingId, setSettingId] = useState<string | null>(null);
  const [values, setValues] = useState<RewardPointsFormValues>({
    ...DEFAULT_REWARD_POINTS_FORM,
  });
  const [savedValues, setSavedValues] = useState<RewardPointsFormValues>({
    ...DEFAULT_REWARD_POINTS_FORM,
  });
  const [errors, setErrors] = useState<Partial<Record<NumberFieldKey, string>>>(
    {}
  );

  const exampleEarnText = useMemo(() => {
    const spend = values.earnSpendAmount || 0;
    const candles = values.earnPoints || 0;
    if (!spend) return 'Configure spend amount to preview earn ratio.';
    return `Example: every ${spend} QAR spent = ${candles} reward candle${
      candles === 1 ? '' : 's'
    }. A 100 QAR product earns about ${
      spend > 0 ? Math.floor((100 / spend) * candles) : 0
    } candles.`;
  }, [values.earnSpendAmount, values.earnPoints]);

  const exampleRedeemText = useMemo(() => {
    const candlesPerQar = Number(values.candlesPerQar || 0);
    if (candlesPerQar <= 0) {
      return 'Enter how many candles equal 1 QAR discount. Example: 40 means 40 candles = 1 QAR.';
    }
  }, [values.candlesPerQar]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await Settings.find({
        query: {
          search: REWARD_POINTS_SETTING_NAME,
          limit: 10,
          skip: 0,
        },
      });
      const setting =
        (response?.data || []).find(
          (item: any) => item.name === REWARD_POINTS_SETTING_NAME
        ) || null;

      if (setting) {
        const parsed = parseRewardPointsValue(setting.value);
        setSettingId(setting.id);
        setValues(parsed);
        setSavedValues(parsed);
        setErrors({});
      } else {
        setSettingId(null);
        setValues({ ...DEFAULT_REWARD_POINTS_FORM });
        setSavedValues({ ...DEFAULT_REWARD_POINTS_FORM });
        setErrors({});
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load reward & referral settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const setFieldError = (field: NumberFieldKey, message?: string) => {
    setErrors((prev) => {
      if (!message) {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: message };
    });
  };

  const handleNumberChange =
    (field: NumberFieldKey) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const parsed = toNumber(raw, 0);

      if (Number.isFinite(parsed) && parsed < 0) {
        setValues((prev) => ({
          ...prev,
          [field]: 0,
        }));
        setFieldError(field, NEGATIVE_ERROR);
        return;
      }

      setValues((prev) => ({
        ...prev,
        [field]: Math.max(0, parsed),
      }));
      setFieldError(field, undefined);
    };

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({
      ...prev,
      activeState: event.target.checked,
    }));
  };

  const handleCancel = () => {
    setValues(savedValues);
    setErrors({});
  };

  const validateBeforeSave = () => {
    const nextErrors: Partial<Record<NumberFieldKey, string>> = {};
    (
      [
        'earnSpendAmount',
        'earnPoints',
        'candlesPerQar',
        'maxRedeemPointsPerOrder',
        'birthdayRewardPoints',
        'birthdayRewardDaysBefore',
        'referralReferrerPoints',
        'referralRefereePoints',
        'pointsExpirationDays',
        'referralSignupExpiryDays',
      ] as NumberFieldKey[]
    ).forEach((field) => {
      if (Number(values[field]) < 0) {
        nextErrors[field] = NEGATIVE_ERROR;
      }
    });

    if (!values.earnSpendAmount || values.earnSpendAmount <= 0) {
      nextErrors.earnSpendAmount = 'Earn spend amount must be greater than 0';
    }

    if (!values.candlesPerQar || values.candlesPerQar <= 0) {
      nextErrors.candlesPerQar =
        'Enter how many candles equal 1 QAR (e.g. 40)';
    }

    if (
      values.birthdayRewardDaysBefore === undefined ||
      values.birthdayRewardDaysBefore === null ||
      String(values.birthdayRewardDaysBefore).trim() === '' ||
      Number.isNaN(Number(values.birthdayRewardDaysBefore))
    ) {
      nextErrors.birthdayRewardDaysBefore =
        'Days before birthday is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) {
      toast.error('Please fix the highlighted fields');
      return;
    }

    setIsSaving(true);
    try {
      const candlesPerQar = Math.max(0, values.candlesPerQar);
      const sanitizedValues = {
        earnSpendAmount: Math.max(0, values.earnSpendAmount),
        earnPoints: Math.max(0, values.earnPoints),
        redeemPointValue: candlesPerQarToRedeemValue(candlesPerQar),
        maxRedeemPointsPerOrder: Math.max(0, values.maxRedeemPointsPerOrder),
        birthdayRewardPoints: Math.max(0, values.birthdayRewardPoints),
        birthdayRewardDaysBefore: Math.max(
          0,
          values.birthdayRewardDaysBefore
        ),
        referralReferrerPoints: Math.max(0, values.referralReferrerPoints),
        referralRefereePoints: Math.max(0, values.referralRefereePoints),
        pointsExpirationDays: Math.max(0, values.pointsExpirationDays),
        referralSignupExpiryDays: Math.max(0, values.referralSignupExpiryDays),
        activeState: values.activeState,
      };

      const payload = {
        name: REWARD_POINTS_SETTING_NAME,
        value: JSON.stringify(sanitizedValues),
        valueType: 'json',
        language: '',
        activeState: true,
      };

      if (settingId) {
        await Settings.patch(settingId, payload);
      } else {
        const created = await Settings.create(payload);
        setSettingId(created?.id || null);
      }

      const formAfterSave: RewardPointsFormValues = {
        ...values,
        candlesPerQar,
      };
      setValues(formAfterSave);
      setSavedValues(formAfterSave);
      setErrors({});
      toast.success('Reward & referral settings saved');
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save reward & referral settings');
    } finally {
      setIsSaving(false);
    }
  };

  const numberFieldProps = (field: NumberFieldKey, helperText?: string) => ({
    fullWidth: true as const,
    value: values[field],
    onChange: handleNumberChange(field),
    error: Boolean(errors[field]),
    helperText: errors[field] || helperText,
    InputProps: {
      inputComponent: NumericFormatCustom as any,
    },
    inputProps: {
      allowNegative: false,
      min: 0,
    },
  });

  return (
    <Page title='Reward & Referral Settings'>
      <Container
        maxWidth='xl'
        sx={{ minWidth: 0, px: { xs: 1.5, sm: 2, md: 3 } }}
      >
        <HeaderBreadcrumbs
          heading='Reward & Referral Settings'
          links={[{ name: 'Dashboard', href: '/' }]}
        />

        <Card sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minWidth: 0 }}>
          {isLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <Loader />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Typography variant='body2' color='text.secondary'>
                These settings drive earn/redeem calculations for the mobile app
                and product reward & referral estimates.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.activeState}
                    onChange={handleActiveChange}
                    color='primary'
                  />
                }
                label='Reward & referral active'
              />

              <Box>
                <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
                  Earning
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Every X QAR spent'
                      {...numberFieldProps('earnSpendAmount')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Equals Y reward candles'
                      {...numberFieldProps('earnPoints')}
                    />
                  </Grid>
                </Grid>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  {exampleEarnText}
                </Typography>
              </Box>

              <Box>
                <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
                  Redemption (checkout discount)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Candles needed for 1 QAR'
                      {...numberFieldProps(
                        'candlesPerQar',
                        'Example: 40 candles = 1 QAR off'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Max candles redeemable per order'
                      {...numberFieldProps(
                        'maxRedeemPointsPerOrder',
                        'Use 0 for no maximum limit'
                      )}
                    />
                  </Grid>
                </Grid>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  {exampleRedeemText}
                </Typography>
              </Box>

              <Box>
                <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
                  Birthday reward
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Birthday reward candles'
                      {...numberFieldProps('birthdayRewardPoints')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Days before birthday'
                      required
                      {...numberFieldProps(
                        'birthdayRewardDaysBefore',
                        'Candles are credited this many days before the birthday (e.g. 6)'
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
                  Referral rewards
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Referring customer candles'
                      {...numberFieldProps('referralReferrerPoints')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Referred customer candles'
                      {...numberFieldProps('referralRefereePoints')}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant='subtitle1' sx={{ mb: 1.5 }}>
                  Expiration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Candles expiration days'
                      {...numberFieldProps(
                        'pointsExpirationDays',
                        'Use 0 for no expiration'
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label='Referral signup expiry days'
                      {...numberFieldProps('referralSignupExpiryDays')}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Stack direction='row' spacing={2} justifyContent='flex-end'>
                <LoadingButton
                  variant='outlined'
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </LoadingButton>
                <LoadingButton
                  variant='contained'
                  loading={isSaving}
                  onClick={handleSave}
                >
                  Save
                </LoadingButton>
              </Stack>
            </Stack>
          )}
        </Card>
      </Container>
    </Page>
  );
};

export default RewardPointsSettingsPage;
